import { DSIconColors } from '@elliemae/ds-icon';
import {
  LockCancelled,
  LockLockedRateRequested,
  LockUnlockedRateRequested,
} from '@elliemae/ds-icons';
import {
  RateRequestStatus,
  RequestLockStatus,
} from '../../common/constants/enumTypes';
import Rights from '../../common/services/Rights';
import { currencyWithDecimals } from '../../common/utils/shared';
import { StyledDangerSpan } from './styled';

// Lock periods are fixed once the loan is locked, unless the user may edit them.
export const isLockPeriodDisabled = (requestLockStatus: unknown): boolean =>
  Number(requestLockStatus) === RequestLockStatus.ActiveLock &&
  !Rights.editLockPeriod;

export const NON_CONVENTIONAL_PRODUCT_TYPES = [2, 3, 4, 6];
export const CONVENTIONAL_PRODUCT_TYPES = [1, 5];

export const shouldShowGetMIColumn = ({
  clientSettings,
  ltv,
  standardProducts,
  loanTerms,
  loanTermsLookup,
  loanChannel,
}: {
  clientSettings?: any;
  ltv?: number;
  standardProducts?: number[];
  loanTerms?: any[];
  loanTermsLookup?: any[];
  loanChannel?: number;
}) => {
  // HELOC will be determined by the loan terms selected
  const isHeloc =
    loanTermsLookup?.length && loanTerms?.length
      ? arrayHasHELOC(loanTermsLookup, loanTerms)
      : false;
  const miViewType = clientSettings?.[0]?.options?.miViewType;
  const miTypes: { miTypeID: number; miType: string }[] =
    clientSettings?.[0]?.miTypes ?? [];
  const miChannelTypes: { loanChannelID: number; channelType: string }[] =
    clientSettings?.[0]?.miChannelTypes ?? [];
  const effectiveLoanChannel = loanChannel === 0 ? 1 : loanChannel;
  const channelAllowed =
    miChannelTypes.length > 0 &&
    miChannelTypes.some((ct) => ct.loanChannelID === effectiveLoanChannel);
  return (
    miViewType &&
    miViewType !== 'off' &&
    (miViewType !== 'bestex' || miTypes.length > 0) &&
    !isHeloc &&
    (ltv ?? 0) > 80 &&
    channelAllowed &&
    CONVENTIONAL_PRODUCT_TYPES.some((productType) =>
      (standardProducts || []).includes(productType),
    )
  );
};

export const getMICertification = (contacts?: any[]) =>
  contacts?.find(
    (c) =>
      c.contactType === 'MORTGAGE_INSURANCE' &&
      c.referenceNumber != null &&
      c.referenceNumber !== '',
  ) ?? null;

export const shouldDisplayAPR = (clientSettings?: any) => {
  const hasRights = Rights.viewAPR;
  const hasClientSetting =
    clientSettings?.[0]?.options?.displayEstimatedAPR === true;
  return hasRights && hasClientSetting;
};

export const dispPrice = (p: number, priceVal: number) => {
  if (Number.isNaN(priceVal) || p === undefined) return 'n/a';
  const result = p.toFixed(3);
  const priceValResult = Math.round(priceVal);
  if (priceValResult < 0) {
    return <StyledDangerSpan>{result}</StyledDangerSpan>;
  }
  return result;
};

export const dispDiscount = (discountPoints) => {
  const result = discountPoints ? discountPoints.toFixed(3) : '';
  if (result < 0) {
    return <StyledDangerSpan>{result}</StyledDangerSpan>;
  }
  return result;
};

export const dispComp = (p) => {
  if (Number.isNaN(p)) return 'n/a';
  return p.toFixed(3);
};

export const USE_ENCOMPASS_APR = 'Use Encompass';

export const dispApr = (p) => {
  // Allow string 'Use Encompass' to be passed in for APR [EPPS-88862]
  if (p === 0) return USE_ENCOMPASS_APR;
  if (p === null || p === undefined || Number.isNaN(p)) return '--';
  if (p === false) return '0.000%';
  return `${p.toFixed(3)}`;
};

export const dispDti = (p) => {
  if (p === null || p === undefined || Number.isNaN(p)) return '--';
  return `${(Number(p) * 100).toFixed(2)}`;
};

export const dispRate = (p, priceVal) => {
  if (p === null || p === undefined || Number.isNaN(p)) return '--';
  if (p === false) return '0.000%';
  if (Number.isNaN(priceVal)) return 'n/a';
  const result = Math.round(priceVal);
  if (result < 0) {
    return `* ${p.toFixed(3)}`;
  }
  return `${p.toFixed(3)}`;
};

export const dispCurrency = (c) => {
  if (Number.isNaN(c)) return 'n/a';
  const result = Math.round(c);
  if (result < 0) {
    return <StyledDangerSpan>{`-$${Math.abs(result)}`}</StyledDangerSpan>;
  }
  return `$${result}`;
};

export const dispCurrencyMask = (c: number) => {
  if (Number.isNaN(c)) return 'n/a';
  const result = Math.round(c);
  if (result < 0) {
    return (
      <StyledDangerSpan>{`(${currencyWithDecimals(
        Math.abs(result),
      )})`}</StyledDangerSpan>
    );
  }
  return `${currencyWithDecimals(result)}`;
};

const getTargetRateOrPriceValue = (loanData) => {
  const loanInfo = loanData.loanInformation;
  const target = loanInfo.target !== undefined ? loanInfo.target : null;
  return Number.isNaN(parseFloat(target)) ? null : parseFloat(target);
};

const getTargetRateOrPriceRadioGroupLabel = (loanData) => {
  const { targetLabel } = loanData.loanInformation;
  return targetLabel;
};

export const getTarget = (loanData) => {
  const targetType = getTargetRateOrPriceRadioGroupLabel(loanData);
  const target = getTargetRateOrPriceValue(loanData) as number;
  let t;
  let bTargetPrice = true;
  if (targetType === 'Rate') {
    t = target;
    bTargetPrice = false;
  } else {
    t = 100 - target;
    bTargetPrice = true;
  }
  return { isTargetPrice: bTargetPrice, value: t };
};

export const applyFilter = (filters) => (p) =>
  (filters.terms === undefined ||
    filters.terms.length === 0 ||
    filters.terms.indexOf(p.programTypeID) > -1) &&
  (filters.standardProducts === undefined ||
    filters.standardProducts.length === 0 ||
    filters.standardProducts.indexOf(p.productTypeID) > -1) &&
  (filters.specialProducts === undefined ||
    filters.specialProducts.length === 0 ||
    filters.specialProducts.indexOf(p.flagFilter) > -1) &&
  (filters.investors === undefined ||
    filters.investors.length === 0 ||
    (p.customLenderID
      ? filters.investors.indexOf(p.customLenderID) > -1
      : filters.investors.indexOf(p.lenderID) > -1)) &&
  (filters.show === undefined || filters.show === 'eligible'
    ? p.deleted === 0
    : p.deleted !== 0);

export const searchUpdater =
  (
    { loanData = {}, quickSearch, dispatch, updateLoanInfo, qualifyPrograms },
    dropDownProductTypes: any[] = [],
  ) =>
  (searchData) => {
    if (loanData) {
      const { productType = [] } = searchData;
      const arrayHasHELOC = dropDownProductTypes
        .filter((elem) => productType.includes(elem.value))
        .some((elem) => elem.heloc);
      const search = {
        ...quickSearch,
        loanType: arrayHasHELOC ? 'HELOC' : 'Conventional',
        productType:
          searchData.productType && searchData.productType.length
            ? searchData.productType
            : quickSearch.productType,
        specialProducts:
          searchData.specialProducts && searchData.specialProducts.length
            ? searchData.specialProducts
            : quickSearch.specialProducts,
        loanInformation: {
          ...quickSearch.loanInformation,
          helocInitialDraw: arrayHasHELOC
            ? quickSearch.loanInformation.helocInitialDraw
            : null,
          target: searchData.target || quickSearch.loanInformation.target,
          targetLabel:
            searchData.targetLabel || quickSearch.loanInformation.targetLabel,
          loanPurpose:
            searchData.loanPurpose !== undefined &&
            searchData.loanPurpose !== null
              ? searchData.loanPurpose
              : quickSearch.loanInformation.loanPurpose,
        },
        productOptions: searchData.productOptions
          ? searchData.productOptions
          : quickSearch.productOptions,
        lockDays:
          searchData.lockDays !== undefined && searchData.lockDays !== null
            ? [searchData.lockDays].flat()
            : (quickSearch.lockDays || []).flat(),
        standardProducts:
          searchData.standardProducts && searchData.standardProducts.length
            ? searchData.standardProducts
            : quickSearch.standardProducts,
      };
      dispatch(updateLoanInfo(search));
      qualifyPrograms(search);
    }
  };

// EPPS-87720: match the locked PROGRAM only, not a specific rateDataID. A
// sellside/relock re-price reassigns the best-ex target rate (and can mint new
// rateDataIDs), so a rate-keyed match flips false the instant the re-priced
// rows render and the lock icon vanishes. The program id is stable across the
// re-price, so the icon persists.
export const checkLockProgramStatus = (relockRequest, programId) => {
  const { specificProgramID } = relockRequest || {};
  if (!specificProgramID || specificProgramID === 0) {
    return false;
  }
  return specificProgramID === programId;
};

export const parseWord = (word = '') => {
  return word.toLocaleLowerCase().replace(/[ ,]/g, '');
};

export const checkLockRateStatus = (relockRequest, programId, rateDataId) => {
  const { specificProgramID, specificRateDataID } = relockRequest;
  if (specificProgramID && specificRateDataID) {
    if (specificProgramID !== 0 && specificRateDataID !== 0) {
      return (
        specificProgramID === programId && specificRateDataID === rateDataId
      );
    }
  }
  return false;
};

const LOCKED_RATE_REQUEST_STATUSES = [
  RateRequestStatus.LockedNoRequest,
  RateRequestStatus.LockedRequest,
  RateRequestStatus.LockedExtensionRequest,
  RateRequestStatus.LockedCancellationRequest,
];

// Drive the icon off rateRequestStatus (the same signal the top ribbon uses)
// rather than the coarse requestLockStatus. requestLockStatus stays Cancelled
// through a relock-after-cancel and cannot distinguish it from a plain
// cancellation; rateRequestStatus flips to NotLocked-Request so the requested
// icon shows instead of the grey cancelled one.
export const getLockIconDisplay = (rateRequestStatus) => {
  const isCancelled = rateRequestStatus === RateRequestStatus.Cancelled;
  const isLocked = LOCKED_RATE_REQUEST_STATUSES.includes(rateRequestStatus);
  if (isCancelled) {
    return {
      LockIcon: LockCancelled,
      color: ['neutral', '200'],
      title: 'Lock Cancelled',
    };
  }
  return {
    LockIcon: isLocked ? LockLockedRateRequested : LockUnlockedRateRequested,
    color: isLocked ? DSIconColors.PRIMARY : DSIconColors.WARNING,
    title: isLocked
      ? 'Rate is locked'
      : 'Rate lock has been requested for this item',
  };
};

export const arrayHasHELOC = (dropDownLoanTerms, loanTerms) =>
  dropDownLoanTerms
    .filter((elem) => loanTerms.includes(elem.value))
    .some((elem) => elem.heloc);

export const isLOComp = () => {
  if (Rights.viewLOComp) {
    return true;
  }
  return false;
};

export const toNumber = (value: string | number | null | undefined): number =>
  typeof value === 'number' ? value : parseFloat(value as string) || 0;
